import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/**
 * TourOrchestrator
 * 
 * Controls the priority queue for overlays:
 * 1. Global tour (first login ever)
 * 2. Mini tour per page (first visit to that page)
 * 3. Contextual popups (offers preferences, etc)
 * 
 * Rules:
 * - Only one overlay at a time
 * - Queue processes in FIFO order within priority
 * - Popup of offers only appears 5s AFTER mini tour ends
 */

const OrchestratorContext = createContext(null);

export function useOrchestrator() {
    return useContext(OrchestratorContext);
}

// Priority levels (lower = higher priority)
const PRIORITY = {
    GLOBAL_TOUR: 1,
    MINI_TOUR: 2,
    CONTEXTUAL_POPUP: 3,
};

export function OrchestratorProvider({ children }) {
    const [activeOverlay, setActiveOverlay] = useState(null);
    const queueRef = useRef([]);
    const processingRef = useRef(false);
    const delayTimerRef = useRef(null);

    const processQueue = useCallback(() => {
        if (processingRef.current) return;
        if (queueRef.current.length === 0) return;

        // Sort by priority
        queueRef.current.sort((a, b) => a.priority - b.priority);
        const next = queueRef.current.shift();

        if (next.delay) {
            delayTimerRef.current = setTimeout(() => {
                setActiveOverlay(next);
                processingRef.current = false;
            }, next.delay);
        } else {
            setActiveOverlay(next);
        }
    }, []);

    const requestOverlay = useCallback((id, priority, delay = 0) => {
        // Don't add duplicates
        if (queueRef.current.some(q => q.id === id)) return;
        if (activeOverlay?.id === id) return;

        queueRef.current.push({ id, priority, delay });

        // If nothing is active, process immediately
        if (!activeOverlay) {
            processQueue();
        }
    }, [activeOverlay, processQueue]);

    const releaseOverlay = useCallback((id) => {
        if (activeOverlay?.id === id) {
            setActiveOverlay(null);
            processingRef.current = false;
            // Dispatch event for listeners
            window.dispatchEvent(new CustomEvent('sv_overlay_released', { detail: { id } }));
            // Process next in queue after small delay
            setTimeout(() => processQueue(), 300);
        } else {
            // Remove from queue if not active
            queueRef.current = queueRef.current.filter(q => q.id !== id);
        }
    }, [activeOverlay, processQueue]);

    const isOverlayActive = useCallback((id) => {
        return activeOverlay?.id === id;
    }, [activeOverlay]);

    const canShow = useCallback((id) => {
        // Can show if nothing is active or this is the active one
        return !activeOverlay || activeOverlay.id === id;
    }, [activeOverlay]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
        };
    }, []);

    const value = {
        activeOverlay,
        requestOverlay,
        releaseOverlay,
        isOverlayActive,
        canShow,
        PRIORITY,
    };

    return (
        <OrchestratorContext.Provider value={value}>
            {children}
        </OrchestratorContext.Provider>
    );
}
